<template>
  <div class="auth-wrapper auth-v2">
    <div class="auth-inner">
      <!-- brand logo -->
      <router-link
        :to="{name: 'login'}"
        class="brand-logo d-flex align-center"
      >
        <v-img
          :src="appLogo"
          max-height="30px"
          max-width="30px"
          alt="logo"
          contain
          class="mr-2"
        ></v-img>

        <h2 class="text--primary">
          {{ appName }}
        </h2>
      </router-link>
      <!--/ brand logo -->

      <!-- reset local storage -->
      <btn
        label="Reset Local Storage"
        class="login-pwa-controls push-left"
        :icon="icons.mdiDeleteForever"
        plain
        @click="resetStorage()"
      ></btn>

      <div class="login-pwa-controls push-right">
        <!-- update app -->
        <btn
          v-if="isProduction && isPWA"
          label="Update App"
          :icon="icons.mdiReload"
          plain
          @click="updateApp()"
        ></btn>

        <!-- note development mode -->
        <icon-value
          v-if="!isProduction"
          :icon="icons.mdiAccountCog"
          value="Dev Mode"
        ></icon-value>

        <!-- update version -->
        <span class="app-version">
          v{{ appVersion }}
        </span>
      </div>

      <v-row class="auth-row ma-0">
        <v-col
          lg="8"
          class="d-none d-lg-block position-relative overflow-hidden pa-0"
        >
          <div class="auth-illustrator-wrapper">
            <!-- 3d character -->
            <div class="d-flex align-center h-full pa-16 pe-0">
              <v-img
                contain
                max-width="100%"
                height="692"
                class="auth-3d-group"
                :src="require(`@/assets/images/3d-characters/login.png`)"
              ></v-img>
            </div>
          </div>
        </v-col>

        <v-col
          lg="4"
          class="d-flex align-center auth-bg pa-5 pb-0"
        >
          <v-row>
            <v-col
              cols="12"
              sm="8"
              md="6"
              lg="12"
              class="mx-auto"
            >
               <div v-if="!isProduction">
                <alert>
                  Development Mode
                </alert>
                <div style="display: flex; justify-content: center; align-items: center">
                  <img
                    src="@/assets/images/logos/blackWhiteCat.png"
                    style="width: 100px; height: 100px;"
                  >
                  "X marks the spot!"
                </div>
              </div>
              <div v-if="isProduction && !isPWA">
                <alert color="error">
                  For proper operation, please install as a PWA!
                </alert>
                <alert color="warning">
                  Make sure the Treasure X PWA isn’t already installed.
                  You should see the following icon on your home screen:
                  <img
                    src="@/assets/images/logos/swc-icon.png"
                    style="width: 25px; height: 25px;"
                    alt="SWC Logo"
                  >
                </alert>

                <alert
                  v-if="isChrome && !isIOS"
                  color="secondary"
                >
                  Chrome PWA installation instructions...
                  <ol class="mt-2">
                    <li>
                      Click the Chrome install icon
                      <v-icon style="height: 20px;">{{ icons.mdiMonitorArrowDownVariant }}</v-icon>
                      on the right side of the URL bar.
                    </li>
                    <li>
                      From the popup, select
                      <b style="color: var(--v-secondary-darken2)">“Install app…”</b>.
                    </li>
                    <li>
                      The app will be added to your desktop...
                    </li>
                  </ol>
                </alert>

                <alert
                  v-if="!isChrome && isIOS"
                  color="secondary"
                >
                  iPad PWA installation instructions...
                  <ol class="mt-2">
                    <li>
                      Tap the <v-icon style="height: 20px; transform: rotate(270deg)">
                        {{ icons.mdiLogout }}
                      </v-icon> icon in the top address bar
                    </li>
                    <li>Select <b style="color: var(--v-secondary-darken2)">Add to Home Screen</b> from the menu</li>
                    <li>Select <b style="color: var(--v-secondary-darken2)">Add</b> to install the PWA</li>
                  </ol>
                  <ul class="mt-2 mb-0">
                    <li>The <b style="color: var(--v-secondary-darken2)">SWC</b> app installs to the home screen</li>
                    <li>You can also drag and drop the app to the dock</li>
                  </ul>
                </alert>
              </div>

              <v-card flat>
                <v-card-text>
                  <p
                    class="text-2xl font-weight-semibold text--primary mb-2"
                  >
                    {{ isProduction ? appTitle : 'Treasure' }}
                    <img
                      v-if="!isProduction"
                      src="@/assets/images/logos/bandaidsX.jpg"
                      style="width: 20px; height: 20px;"
                    >
                  </p>
                  <p class="mb-2">
                    Please sign-in to your account
                  </p>
                  <alert
                    v-if="isIOS"
                    collapsible
                    rounded-border
                    color="primary"
                    border=""
                    font-size="14px"
                    :summary-label="`Reminders (${iosReminders.length})`"
                    class="mb-0 mt-3"
                  >
                    <v-expansion-panels
                      accordion
                      flat
                      class="ios-reminders-panels"
                    >
                      <v-expansion-panel
                        v-for="reminder in iosReminders"
                        :key="reminder.title"
                      >
                        <v-expansion-panel-header class="ios-reminder-header">
                          {{ reminder.title }}
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <p
                            v-for="(paragraph, i) in reminder.body"
                            :key="i"
                            :class="{ 'mb-0': i === reminder.body.length - 1 }"
                          >
                            {{ paragraph }}
                          </p>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </alert>
                </v-card-text>
                <alert
                  v-if="isProduction && !isPWA"
                  color="error"
                >
                  Login is not allowed because the app is not running as a PWA.
                </alert>
                <!-- login form -->
                <v-card-text>
                  <v-form
                    ref="login"
                    v-model="formValid"
                    @submit.prevent="loginClick()"
                  >
                    <text-field
                      v-model="email"
                      :class="{ 'blur-effect' : isProduction && !isPWA}"
                      label="Email"
                      rules="email"
                      email
                      required
                      :disabled="isProduction && !isPWA"
                    ></text-field>


                    <text-field
                      v-model="password"
                      :class="{ 'blur-effect' : isProduction && !isPWA}"
                      label="Password"
                      rules="minLength(8)"
                      password
                      required
                      :disabled="isProduction && !isPWA"
                    ></text-field>

                    <!-- CAPTCHA -->
                    <div v-if="showCaptcha" class="mt-4 mb-2">
                      <captcha :length="5" @update="captchaAnswer = $event" />
                    </div>
                    <text-field
                      v-if="showCaptcha"
                      v-model="captchaInput"
                      label="Enter CAPTCHA"
                      required
                    ></text-field>

                    <div
                      class="d-flex align-center justify-space-between flex-wrap"
                    >
                      <v-checkbox
                        v-model="remember"
                        hide-details
                        label="Remember Me"
                        class="mt-0 pt-0 mb-5"
                        :disabled="isProduction && !isPWA"
                      >
                      </v-checkbox>

                      <!-- forget password link -->
                      <btn
                        label="Forgot Password?"
                        rem-offline-disable
                        plain
                        class="mb-5"
                        @click="forgotPasswordClick()"
                      ></btn>
                    </div>

                    <btn
                      :disabled="!formValid || (isProduction && !isPWA)"
                      label="Login"
                      :icon="icons.mdiLogin"
                      :loading="loading"
                      type="submit"
                      block
                    ></btn>
                    <div class="login-support secondary--text">
                      Trouble logging in? Call
                      <a href="tel:18779999003">877-999-9003 </a>
                    </div>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import Captcha from '@/components/elements/Captcha.vue'
import ResetStorage from '@/mixins/ResetStorage'
import { decryptData, getEncryptionKey } from '@/plugins/storage-encryption'
import {
  mdiAccountCog, mdiCellphoneArrowDown,
  mdiDeleteForever,
  mdiLogin,
  mdiLogout,
  mdiMonitorArrowDownVariant,
  mdiReload
} from '@mdi/js'
import themeConfig from '@themeConfig'
import Vue from 'vue'
import { mapActions } from 'vuex'
import { mapFields } from 'vuex-map-fields'

export default {
  mixins: [ResetStorage],
  components: { Captcha },
  data() {
    return {
      onLine: navigator.onLine,
      formValid: false,
      email: this.remember ? this.$store.state.auth.user.email : '',
      password: '',
      remember: this.$store.state.auth.user.remember,
      captchaInput: '',
      captchaAnswer: '',
      showCaptcha: false,
      appName: themeConfig.app.name,
      appTitle: themeConfig.app.title,
      appLogo: themeConfig.app.logo,
      appVersion: themeConfig.app.version,
      icons: {
        mdiLogin, mdiDeleteForever, mdiReload, mdiAccountCog, mdiCellphoneArrowDown, mdiLogout, mdiMonitorArrowDownVariant
      },
      isPWA: false,
    }
  },
  computed: {
    ...mapFields(['online']),
    ...mapFields('auth', {
      user: 'user',
      loading: 'loading',
      darkMode: 'darkMode',
    }),
    iosReminders() {
      return [
        {
          title: 'Regularly delete items from your photo library',
          body: [
            'iPadOS updates can not be delayed or scheduled, and when they are applied, a slow process begins during which all photos are indexed for searching.',
            'Keeping too many items in the photo library can cause this process to take up to three full days, and Treasure X may become unusable during this period.',
            'Therefore it is recommended that you delete unneeded or old items from your photo library on a weekly basis via the "Photos" app.',
          ],
        },
        {
          title: 'Only update iPadOS right before downtime',
          body: [
            'iPadOS updates can render Treasure X unusable for two full days and can not be delayed or scheduled.',
            'Therefore it is recommended that you install updates specifically prior to a period of non-use during which time you do not need to use the device.',
            'Checking for updates is done via Settings > General > Software Update.',
          ],
        },
      ]
    },
    forgotPasswordUrl() {
      return process.env.VUE_APP_FORGOT_PASSWORD_URL
    },
    isProduction() {
      return process.env.NODE_ENV === 'production'
    },
    isChrome() {
      return /Chrome|CriOS/.test(navigator.userAgent)
    },
    isIOS() {
      if (/iPad|iPhone/.test(navigator.userAgent)) return true

      return /Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 1
    },
  },
  created() {
    this.email = this.remember ? this.$store.state.auth.user.email : ''
  },
  mounted() {
    // Held as plain instance props so they stay non-reactive
    // Vue would otherwise try to walk the MediaQueryList objects.
    this.standaloneQuery = window.matchMedia('(display-mode: standalone)')
    this.minimalUiQuery = window.matchMedia('(display-mode: minimal-ui)')

    this.updateMode()

    this.standaloneQuery.addEventListener('change', this.updateMode)

    // Listen for minimal-ui changes (Firefox)
    this.minimalUiQuery.addEventListener('change', this.updateMode)

    window.addEventListener('online', this.updateOnlineStatus)
    window.addEventListener('offline', this.updateOnlineStatus)
    this.$store.dispatch('auth/setLoading', false)
    this.$store.dispatch('auth/logout').then(() => {
      setTimeout(() => {
        if (this.$route?.query?.notify) {
          this.$store.dispatch('notify', {
            value: this.$route.query?.notify,
            color: this.$route.query?.color ? this.$route.query.color : 'info',
            timeout: this.$route.query?.timeout ? this.$route.query.timeout : 4000,
          })
        }
      }, 100)
    })
  },
  beforeDestroy() {
    this.standaloneQuery?.removeEventListener('change', this.updateMode)
    this.minimalUiQuery?.removeEventListener('change', this.updateMode)
    window.removeEventListener('online', this.updateOnlineStatus)
    window.removeEventListener('offline', this.updateOnlineStatus)
  },
  methods: {
    ...mapActions('auth', ['login']),
    // Checks whether the app is running in PWA mode
    updateMode() {
      this.isPWA =
        this.standaloneQuery.matches ||
        this.minimalUiQuery.matches ||
        window.navigator.standalone === true
    },
    updateOnlineStatus(e) {
      const {
        type,
      } = e
      this.onLine = type === 'online'
    },
    async loginClick(forceRegister) {
      if (!this.$refs.login.validate()) return

      if (this.showCaptcha && (!this.captchaInput || this.captchaInput !== this.captchaAnswer)) {
        Vue.prototype.$custom.processCommError(new Error('CAPTCHA incorrect or missing.'), 'login', true)
        return;
      }

      // Trim and prepare credentials
      const email = this.email.trim().toLowerCase()
      const password = this.password.trim()
      const remember = this.remember
      const appVersion = this.appVersion
      // Check if there's different user data on user auth store
      const hasDifferentUserData = await this.hasDifferentUserData(email)
      if (hasDifferentUserData) {
        this.$root.confirm({
          title: 'Different user data detected.',
          body: `<strong>You entered: <span style=\"color: #d9534f; font-weight: 600;\">${email}</span></strong><br>
                This doesn’t match the saved user data.<br>
                If it’s a typo, please correct it and try again.<br>
                Otherwise, click <strong>“Reset Local Storage”</strong> and log in again.`,
          confirm: 'Okay',
          cancel: false,
          html: true,
        })
        return
      }

      let forceRegisterFromStorage = false;

      const hasUserId = await this.hasUserId();

      if (!hasUserId) {
        forceRegisterFromStorage = await this.forceRegister();

        if (!forceRegisterFromStorage)
          return;
      }

      // This decides when we should provide X-Device-ID
      const registerDevice = forceRegister || forceRegisterFromStorage;

      const proceedLogin = () => {
        this.login({ email, password, remember, registerDevice, appVersion })
        .then(response => {
          if (response?.success) {
            this.$store.dispatch('auth/setRememberEmail', {
              remember: !!remember
            })
            this.$router.push(response.redirect).catch(e => console.log(e))
          }
        })
        .catch(e => {
          this.handleDeviceMismatch(e)
        })
      }

      // TXP-534: Workaround to prevent users from having to log in twice
      // after being logged out due to inactivity.
      const hasQuery = Object.keys(this.$route.query).length > 0
      hasQuery
        ? this.$router.replace({ path: this.$route.path, query: {} }).then(proceedLogin)
        : proceedLogin()
    },
    handleDeviceMismatch(error) {
      const msg = error.message.toLowerCase()

      if (msg.includes('incorrect username or password')) {
        this.showCaptcha = true;
      }

      // Device not on db, send X-Force-Register
      if(msg.includes('device is not registered')) {
        return this.$root.confirm({
          title: 'No device registered',
          body: `Do you wish to register the current instance/device?<br>
                <p style="color: red;">Registering this instance will make it the only active registered instance.
                  All other existing instances will no longer be able to sync data.</p>
          `,
          confirm: 'Register Device',
          html: true,
          textConfirmation: 'Register Device'
        })
        .then(confirm => {
          if (confirm) {
            this.loginClick(true)
          }
        })
      }

      // Device X-Device-ID stored on PWA doesn't match the one stored on the DB
      if(msg.includes('the current device/instance is not registered')) {
        return this.$root.confirm({
          title: 'This instance is not recognized.',
          body: `
            You're currently logged in from a different instance than the one registered to your account.<br>
            If you believe this is your only instance or you’d like to register it, please reset your storage first.`,
          confirm: 'Okay',
          html: true,
          cancel: false,
        })
      }

      // Case where there's information on the db and provider tries to login with outdated instance/device
      if (msg.includes('missing x-device-id')) {
        return this.$root.confirm({
          title: 'This instance is not recognized.',
          body: `You're currently logged in from a different instance than the one registered to your account.<br>
                If you believe this is your only instance or you’d like to register it, please reset your storage first.`,
          confirm: 'Okay',
          html: true,
        })
      }

      // Force reset cases
      if (msg.includes('force reset')) {
        return this.$root.confirm({
          title: 'Storage reset required.',
          body: `A storage reset is required for you to proceed. If you believe this is an issue, please contact the support team.`,
          confirm: 'Okay',
          html: true,
          cancel: false,
        })
      }

      if (msg.includes('network error')) {
        return this.$root.confirm({
          title: 'Connection issue',
          body: `We couldn't complete your login request, likely due to a slow or unstable network connection.<br><br>
                Please check your internet connection and try again. If the problem persists, contact support.`,
          confirm: 'Okay',
          html: true,
        })
      }
    },
    forgotPasswordClick() {
      /*
      this.user.email = this.email
      this.$router.push({ name: 'forgot-password' })
      */

      window.location.href = this.forgotPasswordUrl
    },
    updateApp() {
      this.$store.dispatch('updateApp').then(() => {
        window.location.reload()
        this.$router.replace({ query: {} })
      })
    },
    async hasDifferentUserData(email) {
      // Checks for previous logged in user
      const data = await this.getDecryptedData()

      if (!data)
        return false

      if (data.email && data.email.trim().toLowerCase() != email)
        return true

      return false
    },
    async getDecryptedData() {
      // Returns decrypted user auth store
      const key = await getEncryptionKey()
      const iv = this.$store.state.auth.user.iv
      const encrypted = this.$store.state.auth.user.encrypted

      if (!iv || !encrypted)
        return false

      const data = await decryptData(key, iv, encrypted)

      return data
    },
    async forceRegister() {
      // Force reset prompt will override any existing data or create it accordingly
      return await this.$root.confirm({
        title: 'Register this instance?',
        body: `<p style="color: red;">This will make the current device or PWA the only active instance linked to your account.<br>
                Any other instances will be signed out and will no longer be able to sync or submit records.</p>`,
        confirm: 'Register Device',
        html: true,
        textConfirmation: 'I understand'
      })
      .then(confirm => {
        return confirm;
      })
    },
    async hasUserId() {
      // Check auth.user.id for local storage reset
      const data = await this.getDecryptedData()
      return data?.id
    }
  },

}
</script>

<style lang="scss" scoped>
@import '@core/preset/preset/pages/auth.scss';
.auth-wrapper.auth-v2 .auth-inner .auth-bg .v-card {
  max-width: none !important;
}
.login-pwa-controls {
  position: absolute;
  bottom: 1.83rem;
  line-height: 45px;
  z-index: 1;
  &.push-left {
    left: 2.5rem;
  }
  &.push-right {
    right: 2.5rem;
    .app-version {
      font-size: 15px;
    }
  }
  .icon-value {
    display: inline-block;
    text-transform: uppercase;
    padding-left: 12px;
    padding-right: 12px;
    font-size: 14px;
  }
}
.blur-effect {
  filter: blur(1px);
  pointer-events: none;
}
.login-support {
  margin-top: 12px;
  text-align: center;
  font-size: 0.875rem;
}
.login-support a {
  color: inherit;
  font-weight: 500;
  text-decoration: none;
}
.ios-reminder-header {
  color: var(--v-primary-base);
  font-weight: 600;
  font-size: 14px;
}

.ios-reminders-panels {
  ::v-deep .v-expansion-panel {
    background: transparent !important;
    border-radius: 0 !important;

    &::before {
      box-shadow: none !important;
    }

    &.v-expansion-panel--active.v-expansion-panel--active {
      box-shadow: none !important;
    }

    &:not(:last-child) {
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }
  }

  ::v-deep .v-expansion-panel-header {
    padding: 8px 0;
    min-height: unset;

    &__icon .v-icon {
      height: 18px !important;
      width: 18px !important;
    }
  }

  ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 0 12px;
    color: var(--v-secondary-darken2) !important;
  }
}
</style>
